import Section from 'components/customer/PDF/components/Section';
import {
    CloseButton,
    ProductDetails,
    ProductDetailsWrapper,
    ProductInfoContainer,
    ProductTitle,
    ScrollContainer,
    StyledSection,
} from 'components/customer/Preview3D/components/ProductInfo';
import {useRoomPlannerContext} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import {PLACEHOLDER_NOT_IN_ORDER_LABEL} from 'components/customer/RoomPlanner/helpers/placeholder/copy';
import {getPlaceholderSelectionInfo} from 'components/customer/RoomPlanner/helpers/placeholder/selectionInfo';
import React, {useCallback, useMemo} from 'react';
import {isMobile, isTablet} from 'react-device-detect';
import Icon from 'shared/Icon';
import {useCustomMediaQuery} from 'shared/helpers/DeviceSize';
import styled from 'styled-components';

/**
 * View-only appliance card. Same shell as a cabinet Product Info panel:
 * no edit, move, or delete actions.
 * @return {JSX.Element | null}
 */
export const PlaceholderPreviewInfo = () => {
    const isSmallScreen =
        useCustomMediaQuery({maxWidth: 1024}) || isMobile || isTablet;
    const {selectedCabinetGroup, setSelectedCabinetGroup, placeholderListRef} =
        useRoomPlannerContext();
    const isPlaceholder = Boolean(
        selectedCabinetGroup?.userData?.isPlaceholder
    );

    const close = useCallback(() => {
        setSelectedCabinetGroup?.(null);
    }, [setSelectedCabinetGroup]);

    const {name, dimensions, quantity} = useMemo(
        () =>
            getPlaceholderSelectionInfo(
                selectedCabinetGroup,
                placeholderListRef?.current
            ),
        [selectedCabinetGroup, placeholderListRef]
    );

    if (!isPlaceholder || !selectedCabinetGroup) {
        return null;
    }

    const specValue = (value: number) => (value ? String(value) : '—');

    return (
        <ProductInfoContainer $isOpen $isSmallScreen={isSmallScreen} $is3DRP>
            <ProductDetailsWrapper $is3DRP>
                <ProductDetails $constrained>
                    <ProductTitle>
                        <strong>{name}</strong>
                        <CloseButton
                            type="button"
                            aria-label="Close"
                            onClick={close}>
                            <Icon
                                style={{height: 24, width: 24}}
                                iconName="Button-Cancel-Transparent.svg"
                            />
                        </CloseButton>
                    </ProductTitle>
                    <ScrollContainer $constrained>
                        <Notice>{PLACEHOLDER_NOT_IN_ORDER_LABEL}</Notice>
                        <StyledSection>
                            <Section
                                headerText="SPECS"
                                hasDivider={false}
                                lgValue
                                gap="5px 20px"
                                fields={[
                                    {field: 'Quantity', label: 'QUANTITY'},
                                    {field: 'ProductHeight', label: 'HEIGHT'},
                                    {field: 'ProductWidth', label: 'WIDTH'},
                                    {field: 'ProductDepth', label: 'DEPTH'},
                                ]}
                                data={{
                                    Quantity: String(quantity),
                                    ProductHeight: specValue(dimensions.height),
                                    ProductWidth: specValue(dimensions.width),
                                    ProductDepth: specValue(dimensions.depth),
                                }}
                            />
                        </StyledSection>
                    </ScrollContainer>
                </ProductDetails>
            </ProductDetailsWrapper>
        </ProductInfoContainer>
    );
};

const Notice = styled.div`
    margin: 4px 0 8px;
    font-size: 12px;
    font-weight: 500;
    color: #6b6f70;
    line-height: 1.3;
`;
